import { BUSINESS_TIME_ZONE, type Locale } from '@rental/domain'

/**
 * Formatting for the customer surfaces. Display only: nothing here is ever
 * parsed back into a date or an amount.
 */

/** The Intl locale behind each site language: 24-hour clock and day-first dates everywhere. */
export const INTL_LOCALE: Readonly<Record<Locale, string>> = {
  it: 'it-IT',
  en: 'en-GB',
  de: 'de-DE',
}

export type Params = Readonly<Record<string, string | number>>

const PLACEHOLDER = /\{(\w+)\}/g

/** Replaces each `{name}` with its value. An unknown name stays visible, never empty. */
export function format(template: string, params: Params = {}): string {
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name]
    return value === undefined ? match : String(value)
  })
}

/** The placeholders a message uses, for the translation parity test. */
export function placeholders(template: string): string[] {
  return [...template.matchAll(PLACEHOLDER)].map((match) => match[1] ?? '').sort()
}

/** CLDR plural forms; `other` is the fallback for any category a language leaves out. */
export interface PluralForms {
  readonly one: string
  readonly other: string
  readonly many?: string
}

const pluralRules = new Map<Locale, Intl.PluralRules>()

/** `{count}` is always available to the chosen form. */
export function plural(locale: Locale, forms: PluralForms, count: number, params: Params = {}) {
  let rules = pluralRules.get(locale)
  if (!rules) {
    rules = new Intl.PluralRules(INTL_LOCALE[locale])
    pluralRules.set(locale, rules)
  }
  const category = rules.select(count)
  const template =
    (category === 'one' || category === 'many' ? forms[category] : undefined) ?? forms.other
  return format(template, { count, ...params })
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

function cached<K, V>(cache: Map<K, V>, key: K, create: () => V): V {
  let value = cache.get(key)
  if (value === undefined) {
    value = create()
    cache.set(key, value)
  }
  return value
}

const dateFormats = new Map<string, Intl.DateTimeFormat>()

/**
 * A business date ("YYYY-MM-DD", already a Europe/Rome date) as words. Read
 * at UTC midnight and shown in UTC, so no time zone can move it a day.
 */
export function formatDate(locale: Locale, isoDate: string, style: 'long' | 'short' = 'long') {
  const match = ISO_DATE.exec(isoDate)
  if (!match) return isoDate
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  const formatter = cached(
    dateFormats,
    `${locale}:${style}`,
    () =>
      new Intl.DateTimeFormat(INTL_LOCALE[locale], {
        timeZone: 'UTC',
        weekday: 'short',
        day: 'numeric',
        month: style === 'long' ? 'long' : 'short',
        year: 'numeric',
      }),
  )
  return formatter.format(date)
}

const dayMonthFormats = new Map<Locale, Intl.DateTimeFormat>()

/**
 * A business date as the lock screen's clock says it: the weekday and the day
 * of the month, in words, no year ("domenica 18 ottobre"). Read and shown in
 * UTC, as formatDate.
 */
export function formatDayMonth(locale: Locale, isoDate: string): string {
  const match = ISO_DATE.exec(isoDate)
  if (!match) return isoDate
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  const formatter = cached(
    dayMonthFormats,
    locale,
    () =>
      new Intl.DateTimeFormat(INTL_LOCALE[locale], {
        timeZone: 'UTC',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }),
  )
  return formatter.format(date)
}

const monthFormats = new Map<Locale, Intl.DateTimeFormat>()

/** A business month ("YYYY-MM") as words: "ottobre 2026". Read and shown in UTC, as formatDate. */
export function formatMonth(locale: Locale, isoMonth: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(isoMonth)
  if (!match) return isoMonth
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  const formatter = cached(
    monthFormats,
    locale,
    () =>
      new Intl.DateTimeFormat(INTL_LOCALE[locale], {
        timeZone: 'UTC',
        month: 'long',
        year: 'numeric',
      }),
  )
  return formatter.format(date)
}

const dateTimeFormats = new Map<Locale, Intl.DateTimeFormat>()

/** An instant as the Europe/Rome date and time, whatever the visitor's own zone. */
export function formatDateTime(locale: Locale, instant: string | Date): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant
  if (Number.isNaN(date.getTime())) return String(instant)
  const formatter = cached(
    dateTimeFormats,
    locale,
    () =>
      new Intl.DateTimeFormat(INTL_LOCALE[locale], {
        timeZone: BUSINESS_TIME_ZONE,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
  )
  return formatter.format(date)
}

const timeFormats = new Map<Locale, Intl.DateTimeFormat>()

/** An instant as the Europe/Rome time of day ("09:30"): for lists that already show the day. */
export function formatTime(locale: Locale, instant: string | Date): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant
  if (Number.isNaN(date.getTime())) return String(instant)
  const formatter = cached(
    timeFormats,
    locale,
    () =>
      new Intl.DateTimeFormat(INTL_LOCALE[locale], {
        timeZone: BUSINESS_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
  )
  return formatter.format(date)
}

const regionNames = new Map<Locale, Intl.DisplayNames>()

/** A country's name in the visitor's language; the code itself when the runtime has no name. */
export function countryName(locale: Locale, code: string): string {
  const names = cached(
    regionNames,
    locale,
    () => new Intl.DisplayNames([INTL_LOCALE[locale]], { type: 'region', fallback: 'code' }),
  )
  try {
    return names.of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** Every value of a `{ it, en, de }` field from the API, in the visitor's language. */
export function localized(text: Readonly<Record<Locale, string>>, locale: Locale): string {
  return text[locale]
}
